
import React, { useState, useEffect } from 'react';
import '../../Styles/ManagementPages.css';
import { 
  createInfluencer, 
  getInfluencers, 
  getInfluencerById, 
  updateInfluencer, 
  deleteInfluencer,
  updateInfluencerStatus 
} from '../../Services/Operations/ProductAPI';
import { useSelector } from 'react-redux';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>Influencer</th>
          <th>Contact</th>
          <th>Commission Rate</th>
          <th>Referrals</th>
          <th>Earnings</th>
          <th>Joined</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonFilterPanel = () => (
  <div className="filter-panel skeleton">
    <div className="filter-header">
      <div className="skeleton-title"></div>
      <div className="skeleton-close"></div>
    </div>
    <div className="filter-grid">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
        <div key={i} className="filter-group">
          <div className="skeleton-label"></div>
          <div className="skeleton-input"></div>
        </div>
      ))}
    </div>
    <div className="filter-actions">
      <div className="skeleton-button"></div>
      <div className="skeleton-button"></div>
    </div>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
          <div key={i} className="skeleton-detail-row">
            <div className="skeleton-label"></div>
            <div className="skeleton-value"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const InfluencerManage = () => {
  const [influencers, setInfluencers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [editingInfluencer, setEditingInfluencer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [viewingInfluencer, setViewingInfluencer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [processingPayout, setProcessingPayout] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [newInfluencer, setNewInfluencer] = useState({
    name: '',
    email: '',
    phone: '',
    commissionRate: 10
  });

  const { token } = useSelector(state => state.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInfluencers, setTotalInfluencers] = useState(0);
  const [pageSize] = useState(10);

  const [filters, setFilters] = useState({
    status: 'All Status',
    name: '',
    email: '',
    phone: '',
    referralCode: '',
    minCommission: '',
    maxCommission: '',
    minEarnings: '',
    maxEarnings: '',
    dateFrom: '',
    dateTo: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchInfluencers();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, filters]);

  const fetchInfluencers = async () => {
    try {
      setSearching(true);
      setLoading(true);
      setError(null);
      
      const filterParams = {};
      
      if (searchTerm) {
        filterParams.search = searchTerm;
      }
      
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'All Status') {
          filterParams[key] = filters[key];
        }
      });

      const params = {
        page: currentPage,
        limit: pageSize,
        ...filterParams
      };

      console.log('Fetching influencers with params:', params);

      const response = await getInfluencers(token, params);
      if (response && response.influencers) {
        const influencersWithDefaults = response.influencers.map(infl => ({
          ...infl,
          isActive: infl.isActive !== undefined ? infl.isActive : true,
          stats: infl.stats || {
            totalEarnings: 0,
            pendingEarnings: 0,
            totalReferrals: 0
          }
        }));
        setInfluencers(influencersWithDefaults);
        
        if (response.totalPages) {
          setTotalPages(response.totalPages);
        }
        if (response.total) {
          setTotalInfluencers(response.total);
        }

        const hasActiveFilters = 
          filters.status !== 'All Status' || 
          searchTerm !== '' || 
          Object.values(filters).some(value => 
            value && value !== '' && value !== 'All Status'
          );
        setIsFiltered(hasActiveFilters);
      } else {
        setError('Failed to fetch influencers.');
        setInfluencers([]);
      }
    } catch (err) {
      console.error('Error fetching influencers:', err);
      setError('Failed to fetch influencers. Please try again.');
      setInfluencers([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const fetchInfluencerById = async (id) => {
    try {
      const response = await getInfluencerById(id, token);
      return response;
    } catch (error) {
      console.error('Error fetching influencer:', error);
      throw error;
    }
  };

  const getSafeString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const getSafeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    return Number(value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchInfluencers();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchInfluencers();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      status: 'All Status',
      name: '',
      email: '',
      phone: '',
      referralCode: '',
      minCommission: '',
      maxCommission: '',
      minEarnings: '',
      maxEarnings: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
    setIsFiltered(false);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleExportCSV = () => {
    if (influencers.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Name', 'Email', 'Phone', 'Referral Code', 'Commission Rate', 'Total Referrals', 'Total Earnings', 'Pending Earnings', 'Status', 'Joined Date'];
    const csvData = influencers.map(influencer => [
      influencer.name || '',
      influencer.email || '',
      influencer.phone || '',
      influencer.referralCode || '',
      `${influencer.commissionRate || 0}%`,
      influencer.stats?.totalReferrals || 0,
      influencer.stats?.totalEarnings || 0,
      influencer.stats?.pendingEarnings || 0,
      influencer.isActive ? 'Active' : 'Inactive',
      formatDate(influencer.createdAt) || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `influencers-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('CSV exported successfully!');
  };

  const handleViewInfluencer = async (influencer) => {
    try {
      setIsFetchingDetails(true);
      const fullInfluencerData = await fetchInfluencerById(influencer._id);
      setViewingInfluencer(fullInfluencerData);
    } catch (error) {
      console.error('Error fetching influencer details:', error);
      alert('Failed to load influencer details');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditInfluencer = (influencer) => {
    setEditingInfluencer(influencer._id);
    setEditForm({ 
      name: influencer.name,
      email: influencer.email,
      phone: influencer.phone,
      commissionRate: influencer.commissionRate
    });
  };

  const handleSaveEdit = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await updateInfluencer(editingInfluencer, editForm, token);
      await fetchInfluencers();
      setEditingInfluencer(null);
      setEditForm({});
      alert('Influencer updated successfully!');
    } catch (err) {
      console.error('Error updating influencer:', err);
      setError('Failed to update influencer. Please try again.');
      alert('Failed to update influencer. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingInfluencer(null);
    setEditForm({});
    setError(null);
  };

  const handleProcessPayout = (influencer) => {
    setProcessingPayout(influencer);
  };

  const handleConfirmPayout = () => {
    setInfluencers(prevInfluencers =>
      prevInfluencers.map(influencer =>
        influencer._id === processingPayout._id ? { 
          ...influencer, 
          stats: {
            ...influencer.stats,
            pendingEarnings: 0
          }
        } : influencer
      )
    );
    setProcessingPayout(null);
    alert(`Payout processed successfully!`);
  };

  const handleAddInfluencer = async () => {
    if (!newInfluencer.name || !newInfluencer.email || !newInfluencer.phone) {
      alert('Please fill in all required fields: Name, Email, and Phone');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await createInfluencer(newInfluencer, token);
      await fetchInfluencers();
      setShowAddModal(false);
      setNewInfluencer({
        name: '',
        email: '',
        phone: '',
        commissionRate: 10
      });
      
      const successMessage = `Influencer created successfully!\n\nReferral Code: ${response.influencer.referralCode}\nTemporary Password: ${response.influencer.tempPassword}\n\nPlease share these credentials with the influencer.`;
      alert(successMessage);
    } catch (err) {
      console.error('Error creating influencer:', err);
      const errorMessage = err.response?.data?.message || 'Failed to create influencer. Please try again.';
      setError(`Error: ${errorMessage}`);
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteInfluencer = async (influencerId) => {
    if (window.confirm('Are you sure you want to delete this influencer?')) {
      try {
        setError(null);
        await deleteInfluencer(influencerId, token);
        await fetchInfluencers();
        alert('Influencer deleted successfully!');
      } catch (err) {
        console.error('Error deleting influencer:', err);
        setError('Failed to delete influencer. Please try again.');
        alert('Failed to delete influencer. Please try again.');
      }
    }
  };

  const handleToggleStatus = async (influencer) => {
    try {
      const newStatus = !influencer.isActive;
      await updateInfluencerStatus(influencer._id, { isActive: newStatus }, token);
      await fetchInfluencers();
      alert(`Influencer ${newStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error updating influencer status:', error);
      alert('Failed to update influencer status');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: name === 'commissionRate' ? parseInt(value) : value
    }));
  };

  const handleNewInfluencerChange = (e) => {
    const { name, value } = e.target;
    setNewInfluencer(prev => ({
      ...prev,
      [name]: name === 'commissionRate' ? parseInt(value) : value
    }));
  };

  const closeViewModal = () => {
    setViewingInfluencer(null);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewInfluencer({
      name: '',
      email: '',
      phone: '',
      commissionRate: 10
    });
    setError(null);
  };

  const closePayoutModal = () => {
    setProcessingPayout(null);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    clearFilters();
  };

  const activeInfluencers = influencers.filter(i => i.isActive).length;
  const totalCommission = influencers.reduce((sum, influencer) => sum + getSafeNumber(influencer.stats?.pendingEarnings), 0);
  const totalEarnings = influencers.reduce((sum, influencer) => sum + getSafeNumber(influencer.stats?.totalEarnings), 0);
  const totalReferrals = influencers.reduce((sum, influencer) => sum + getSafeNumber(influencer.stats?.totalReferrals), 0);

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'N/A';
    }
  };

  if (loading && influencers.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Influencer Management</h1>
        <div className="header-actions">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div style={{ position: 'relative' }}>
              <input 
                type="text" 
                placeholder="Search by name, email or phone..." 
                className="search-input" 
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: "300px" }}
              />
              {searching && (
                <span style={{
                  position: 'absolute', right: '10px', top: '50%',
                  transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
                }}>
                  searching...
                </span>
              )}
            </div>
          </form>
          
          <button className="filter-btn" onClick={toggleFilters}>
            🔍 Filters
          </button>
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
          <button className="add-btn" onClick={() => setShowAddModal(true)}>
            + Add Influencer
          </button>
        </div>
      </div>


      {showFilters && !loading && (
        <div className="filter-panel">
          <div className="filter-header">
            <h3>Filter Influencers</h3>
            <button className="close-filters" onClick={() => setShowFilters(false)}>
              ×
            </button>
          </div>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Name</label>
              <input
                type="text"
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
                placeholder="Enter name"
              />
            </div>
            <div className="filter-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                placeholder="Enter email"
              />
            </div>
            <div className="filter-group">
              <label>Phone</label>
              <input
                type="text"
                name="phone"
                value={filters.phone}
                onChange={handleFilterChange}
                placeholder="Enter phone"
              />
            </div>
            <div className="filter-group">
              <label>Referral Code</label>
              <input
                type="text"
                name="referralCode"
                value={filters.referralCode}
                onChange={handleFilterChange}
                placeholder="Enter referral code"
              />
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="All Status">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Min Commission (%)</label>
              <input
                type="number"
                name="minCommission"
                value={filters.minCommission}
                onChange={handleFilterChange}
                placeholder="Minimum commission"
                min="0"
                max="100"
              />
            </div>
            <div className="filter-group">
              <label>Max Commission (%)</label>
              <input
                type="number"
                name="maxCommission"
                value={filters.maxCommission}
                onChange={handleFilterChange}
                placeholder="Maximum commission"
                min="0"
                max="100"
              />
            </div>
            <div className="filter-group">
              <label>Min Earnings</label>
              <input
                type="number"
                name="minEarnings"
                value={filters.minEarnings}
                onChange={handleFilterChange}
                placeholder="Minimum earnings"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>Max Earnings</label>
              <input
                type="number"
                name="maxEarnings"
                value={filters.maxEarnings}
                onChange={handleFilterChange}
                placeholder="Maximum earnings"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>Joined From</label>
              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Joined To</label>
              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="apply-filters-btn" onClick={applyFilters}>
              Apply Filters
            </button>
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All
            </button>
          </div>
          {isFiltered && (
            <div className="active-filters">
              <span>Active Filters:</span>
              {searchTerm && (
                <span className="filter-tag">Search: {searchTerm}</span>
              )}
              {filters.status !== 'All Status' && (
                <span className="filter-tag">Status: {filters.status}</span>
              )}
              {Object.entries(filters).map(([key, value]) => 
                value && 
                key !== 'status' && 
                key !== 'dateFrom' && 
                key !== 'dateTo' && (
                  <span key={key} className="filter-tag">
                    {key}: {value}
                  </span>
                )
              )}
              {filters.dateFrom && (
                <span className="filter-tag">From: {filters.dateFrom}</span>
              )}
              {filters.dateTo && (
                <span className="filter-tag">To: {filters.dateTo}</span>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Influencers</h3>
          <p>{totalInfluencers}</p>
          <span>All marketing partners</span>
        </div>
        <div className="stat-card">
          <h3>Active</h3>
          <p>{activeInfluencers}</p>
          <span>Active influencers</span>
        </div>
        <div className="stat-card">
          <h3>Pending Payout</h3>
          <p>₹{totalCommission.toLocaleString()}</p>
          <span>awaiting payment</span>
        </div>
        <div className="stat-card">
          <h3>Search Results</h3>
          <p>{influencers.length}</p>
          <span>influencers found</span>
        </div>
      </div>


      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Influencer</th>
              <th>Contact</th>
              <th>Commission Rate</th>
              <th>Referrals</th>
              <th>Earnings</th>
              <th>Joined</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {influencers.length > 0 ? (
              influencers.map(influencer => (
                <tr key={influencer._id}>
                  <td>
                    {editingInfluencer === influencer._id ? (
                      <input
                        type="text"
                        name="name"
                        value={editForm.name || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                      />
                    ) : (
                      <div>
                        <div className="influencer-name">{getSafeString(influencer.name)}</div>
                        {influencer.referralCode && (
                          <div className="referral-code" style={{color: '#666', fontSize: '12px', marginTop: '2px'}}>
                            Code: {getSafeString(influencer.referralCode)}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {editingInfluencer === influencer._id ? (
                      <div>
                        <input
                          type="email"
                          name="email"
                          value={editForm.email || ''}
                          onChange={handleInputChange}
                          className="edit-input"
                          placeholder="Email"
                        />
                        <input
                          type="text"
                          name="phone"
                          value={editForm.phone || ''}
                          onChange={handleInputChange}
                          className="edit-input"
                          placeholder="Phone"
                          style={{marginTop: '5px'}}
                        />
                      </div>
                    ) : (
                      <div>
                        <div className="contact-email">{getSafeString(influencer.email)}</div>
                        <div className="contact-phone" style={{color: '#666', fontSize: '12px', marginTop: '2px'}}>
                          {getSafeString(influencer.phone)}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    {editingInfluencer === influencer._id ? (
                      <select
                        name="commissionRate"
                        value={editForm.commissionRate || ''}
                        onChange={handleInputChange}
                        className="edit-select"
                      >
                        <option value={8}>8%</option>
                        <option value={10}>10%</option>
                        <option value={12}>12%</option>
                        <option value={13}>13%</option>
                        <option value={15}>15%</option>
                      </select>
                    ) : (
                      <span className="commission-rate">{getSafeNumber(influencer.commissionRate)}%</span>
                    )}
                  </td>
                  <td>
                    <span className="referrals-count">
                      {getSafeNumber(influencer.stats?.totalReferrals)}
                    </span>
                  </td>
                  <td>
                    <div className="earnings-info">
                      <div className="total-earnings">Total: ₹{getSafeNumber(influencer.stats?.totalEarnings)}</div>
                      {influencer.stats?.pendingEarnings > 0 && (
                        <div className="pending-earnings" style={{color: '#48bb78', fontWeight: '600', fontSize: '12px', marginTop: '2px'}}>
                          Pending: ₹{getSafeNumber(influencer.stats.pendingEarnings)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="join-date">
                      {formatDate(influencer.createdAt)}
                    </span>
                  </td>
                  <td>
                    <span 
                      className={`status-badge ${influencer.isActive ? 'active' : 'inactive'}`}
                      style={{cursor: 'pointer'}}
                      onClick={() => handleToggleStatus(influencer)}
                      title={`Click to ${influencer.isActive ? 'deactivate' : 'activate'}`}
                    >
                      {influencer.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {editingInfluencer === influencer._id ? (
                      <div className="edit-actions">
                        <button 
                          className="action-btn save" 
                          onClick={handleSaveEdit}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Saving...' : 'Save'}
                        </button>
                        <button className="action-btn cancel" onClick={handleCancelEdit}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button 
                          className="action-btn view" 
                          onClick={() => handleViewInfluencer(influencer)}
                          title="View Profile"
                        >
                          Profile
                        </button>
                        <button 
                          className="action-btn edit" 
                          onClick={() => handleEditInfluencer(influencer)}
                          title="Edit Influencer"
                        >
                          Edit
                        </button>
                        {influencer.stats?.pendingEarnings > 0 && (
                          <button 
                            className="action-btn payout" 
                            onClick={() => handleProcessPayout(influencer)}
                            title="Process Payout"
                          >
                            Payout
                          </button>
                        )}
                        <button 
                          className="action-btn delete" 
                          onClick={() => handleDeleteInfluencer(influencer._id)}
                          title="Delete Influencer"
                          style={{backgroundColor: '#e53e3e'}}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  <p>No influencers found {isFiltered ? 'matching your filters' : ''}.</p>
                  {isFiltered && (
                    <button className="clear-filters-link" onClick={clearFilters}>
                      Clear filters to see all influencers
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      <div className="pagination-container">
        <div className="pagination-info">
          Showing {influencers.length} of {totalInfluencers} influencers
          (Page {currentPage} of {totalPages})
          {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
          {isFiltered && !searchTerm && <span className="filtered-indicator"> • Filtered Results</span>}
        </div>
        <div className="pagination-controls">
          <button 
            className="pagination-btn" 
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {generatePageNumbers().map(pageNumber => (
              <button
                key={pageNumber}
                className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                onClick={() => handlePageClick(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="page-dots">...</span>
                <button
                  className="page-number"
                  onClick={() => handlePageClick(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>
          
          <button 
            className="pagination-btn" 
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>


      {viewingInfluencer && !isFetchingDetails && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Influencer Profile - {getSafeString(viewingInfluencer.influencer?.name)}</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>Influencer Name:</label>
                  <span>{getSafeString(viewingInfluencer.influencer?.name)}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{getSafeString(viewingInfluencer.influencer?.email)}</span>
                </div>
                <div className="detail-row">
                  <label>Phone:</label>
                  <span>{getSafeString(viewingInfluencer.influencer?.phone)}</span>
                </div>
                <div className="detail-row">
                  <label>Commission Rate:</label>
                  <span>{getSafeNumber(viewingInfluencer.influencer?.commissionRate)}%</span>
                </div>
                <div className="detail-row">
                  <label>Referral Code:</label>
                  <span style={{fontWeight: 'bold', color: '#1890ff'}}>
                    {getSafeString(viewingInfluencer.influencer?.referralCode)}
                  </span>
                </div>
                {viewingInfluencer.influencer?.tempPassword && (
                  <div className="detail-row">
                    <label>Temporary Password:</label>
                    <span style={{fontWeight: 'bold', color: '#dc2626'}}>
                      {getSafeString(viewingInfluencer.influencer.tempPassword)}
                    </span>
                  </div>
                )}
                <div className="detail-row">
                  <label>Total Referrals:</label>
                  <span>{getSafeNumber(viewingInfluencer.stats?.totalReferrals)}</span>
                </div>
                <div className="detail-row">
                  <label>Total Earnings:</label>
                  <span>₹{getSafeNumber(viewingInfluencer.stats?.totalEarnings)}</span>
                </div>
                <div className="detail-row">
                  <label>Pending Earnings:</label>
                  <span>₹{getSafeNumber(viewingInfluencer.stats?.pendingEarnings)}</span>
                </div>
                <div className="detail-row">
                  <label>Joined Date:</label>
                  <span>{formatDate(viewingInfluencer.influencer?.createdAt)}</span>
                </div>
                <div className="detail-row">
                  <label>Last Updated:</label>
                  <span>{formatDate(viewingInfluencer.influencer?.updatedAt)}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge ${viewingInfluencer.influencer?.isActive ? 'active' : 'inactive'}`}>
                    {viewingInfluencer.influencer?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {viewingInfluencer && isFetchingDetails && <SkeletonModal />}


      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Influencer</h3>
              <button className="modal-close-btn" onClick={closeAddModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>Influencer Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={newInfluencer.name}
                    onChange={handleNewInfluencerChange}
                    className="edit-input"
                    placeholder="Enter influencer name"
                    required
                  />
                </div>
                <div className="detail-row">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={newInfluencer.email}
                    onChange={handleNewInfluencerChange}
                    className="edit-input"
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div className="detail-row">
                  <label>Phone *</label>
                  <input
                    type="text"
                    name="phone"
                    value={newInfluencer.phone}
                    onChange={handleNewInfluencerChange}
                    className="edit-input"
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                <div className="detail-row">
                  <label>Commission Rate *</label>
                  <select
                    name="commissionRate"
                    value={newInfluencer.commissionRate}
                    onChange={handleNewInfluencerChange}
                    className="edit-select"
                    required
                  >
                    <option value={8}>8%</option>
                    <option value={10}>10%</option>
                    <option value={12}>12%</option>
                    <option value={13}>13%</option>
                    <option value={15}>15%</option>
                  </select>
                </div>
              </div>
              <div style={{marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px', fontSize: '12px', color: '#666'}}>
                <p style={{margin: '0'}}><strong>Note:</strong> Fields marked with * are required. The influencer will receive a referral code and temporary password upon creation.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={closeAddModal} disabled={isLoading}>
                Cancel
              </button>
              <button 
                className="action-btn save" 
                onClick={handleAddInfluencer}
                disabled={isLoading || !newInfluencer.name || !newInfluencer.email || !newInfluencer.phone}
              >
                {isLoading ? 'Creating...' : 'Add Influencer'}
              </button>
            </div>
          </div>
        </div>
      )}


      {processingPayout && (
        <div className="modal-overlay" onClick={closePayoutModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Process Payout</h3>
              <button className="modal-close-btn" onClick={closePayoutModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>Influencer:</label>
                  <span>{getSafeString(processingPayout.name)}</span>
                </div>
                <div className="detail-row">
                  <label>Referral Code:</label>
                  <span>{getSafeString(processingPayout.referralCode)}</span>
                </div>
                <div className="detail-row">
                  <label>Amount to Pay:</label>
                  <span style={{color: '#48bb78', fontWeight: '600', fontSize: '18px'}}>
                    ₹{getSafeNumber(processingPayout.stats?.pendingEarnings)}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Total Earnings:</label>
                  <span>₹{getSafeNumber(processingPayout.stats?.totalEarnings)}</span>
                </div>
              </div>
              <div style={{marginTop: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px'}}>
                <p style={{margin: '0', color: '#0369a1', fontSize: '14px'}}>
                  <strong>Note:</strong> This will process the payment and reset the pending commission to zero.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={closePayoutModal}>
                Cancel
              </button>
              <button className="action-btn payout" onClick={handleConfirmPayout}>
                Confirm Payout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfluencerManage;